import { Injectable } from '@angular/core';
import { Market, ProductPlatform, Region } from "../../model/shared/platform-region.model";
import { Role, UserProfileResponse } from '../../model/shared/user.model';
import { catchError, firstValueFrom, map, Observable, shareReplay, Subject, throwError } from 'rxjs';
import { ADMIN_SERVICE } from '../service-url';
import { HttpClient } from '@angular/common/http';
import { RoleStateService } from '../../v2/services/core/role-state.service';

export type DefaultBUPayload = {
  wwid: number;
  business_unit_code: string;
  default_primary_market: string;
  default_primary_region: string;
  default_business_unit_code: string;
  is_default_bu: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PlatformRegionService {

  private marketSubject = new Subject<void>();
  marketSubject$: Observable<void> = this.marketSubject.asObservable();

  private rolesListSubject = new Subject<Role[]>();
  rolesListChanged$: Observable<Role[]> = this.rolesListSubject.asObservable();

  private product_platform_list: ProductPlatform[] = [];
  private regions_list: Region[] = [];
  private roles_list: Role[] = [];
  private market_list: Market[] = [];

  private selected_platform: ProductPlatform | null = null;
  private selected_region: Region | null = null;
  private selected_role: Role | null = null;
  private selected_market: Market | null = null;

  selected_platform_code: string | null = null;
  selected_region_code: string | null = null;
  selected_region_label: string | null = null;
  selected_market_code: string | null = null;

  default_platform_code: string | null = null;
  default_region_code: string | null = null;
  default_market_code: string | null = null;

  constructor(private httpClient: HttpClient, private roleStateService: RoleStateService) { }

  get productPlatformList(): ProductPlatform[] {
    return this.product_platform_list;
  }

  set productPlatformList(product_platform_list: ProductPlatform[]) {
    this.product_platform_list = product_platform_list;
  }

  get selectedProductPlatform(): ProductPlatform | null {
    return this.selected_platform;
  }

  get regionsList(): Region[] {
    return this.regions_list;
  }

  set regionsList(regions: Region[]) {
    this.regions_list = regions;
  }

  get selectedRegion(): Region | null {
    return this.selected_region;
  }

  set selectedRegion(region: Region) {
    this.selected_region = region;
  }

  get rolesList(): Role[] {
    return this.roles_list;
  }

  set rolesList(roles: Role[]) {
    this.roles_list = roles;
  }

  get selectedRole(): Role | null {
    return this.selected_role;
  }

  set selectedRole(role: Role) {
    this.selected_role = role;
    this.setRoleDataInSession();
  }

  get marketList(): Market[] {
    return this.market_list;
  }

  set marketList(market_list: Market[]) {
    this.market_list = market_list;
  }


  getPlatformProductByProductCode(platformCode: string): ProductPlatform | null {
    return this.productPlatformList.find(product => product.business_unit_code == platformCode) || null;
  }

  setUserDefaultData(userProfile: UserProfileResponse) {
    this.productPlatformList = userProfile.product;
    if ((userProfile.default_business_unit_code)) {
      this.setDefaultProductPlatformCode(userProfile.default_business_unit_code);
    }
  }


  setIntialProductPlatform() {
    if (this.productPlatformList && this.productPlatformList.length == 1) {
      this.setSelectedPlatformProductByProductCode(this.productPlatformList[0].business_unit_code)
    } else if (this.default_platform_code) {
      this.setSelectedPlatformProductByProductCode(this.default_platform_code)
    }
  }

  setSelectedPlatformProductByProductCode(platformCode: string) {
    const productPlatform: ProductPlatform = this.getPlatformProductByProductCode(platformCode);
    this.setSelectedProductPlatform(productPlatform);
  }

  setSelectedProductPlatform(productPlatform: ProductPlatform) {
    this.selected_platform = productPlatform;
    this.regionsList = productPlatform.regions;
    this.setDefaultRegionAndMarketCode(productPlatform);
    this.setSelectedPlatformCodeByProductPlatform(productPlatform)
    this.setDataForSelectedRegion(productPlatform.user_default_region);
  }

  setDataForSelectedRegion(region_code: string) {
    region_code = region_code || this.regions_list[0].name;

    const region = this.regionsList.find(region => region.name === region_code || region.label === region_code) || this.regions_list[0];
    this.setSelectedRegion(region);
  }

  setDefaultRegionAndMarketCode(productPlatform: ProductPlatform) {
    this.setDefaultRegionCode(productPlatform.user_default_region)
    this.setDefaultMarketCode(productPlatform.user_default_market)
  }

  setSelectedPlatformCodeByProductPlatform(productPlatform: ProductPlatform) {
    this.setSelectedProductPlatformCode(productPlatform.business_unit_code);
    this.setSelectedRegionCode(productPlatform.user_default_region)
    this.setSelectedMarketCode(productPlatform.user_default_market)
  }

  setDefaultPlatformCode(platformCode: string, regionCode: string, marketCode: string) {
    this.setDefaultProductPlatformCode(platformCode);
    this.setDefaultRegionCode(regionCode)
    this.setDefaultMarketCode(marketCode)
  }

  setDefaultProductPlatformCode(platformCode: string) {
    this.default_platform_code = platformCode || null;
  }

  setDefaultRegionCode(regionCode: string) {
    this.default_region_code = regionCode || null;
  }

  setDefaultMarketCode(marketCode: string) {
    this.default_market_code = marketCode || null;
  }

  setSelectedProductPlatformCode(platformCode: string) {
    this.selected_platform_code = platformCode || null;
  }

  setSelectedRegionCode(regionCode: string) {
    this.selected_region_code = regionCode || null;
  }

  setSelectedMarketCode(marketCode: string) {
    this.selected_market_code = marketCode || null;
  }

  setSelectedRegion(region: Region) {
    this.setSelectedRegionCode(region.name);
    this.selected_region_label = region.label || region.name;
    this.selectedRegion = region;
    this.setSelectedMarket();
    this.setSelectedRole();
  }

  setSelectedRole() {
    this.rolesList = this.selectedRegion.roles;
    this.selectedRole = this.rolesList[0];
    this.rolesListSubject.next(this.rolesList);
    this.roleStateService.setAvailableRoles(this.rolesList);
  }

  setRoleDataInSession() {
    sessionStorage.setItem('displayRole', this.selectedRole.role_label);
    sessionStorage.setItem('role', this.selectedRole.role_name);
    sessionStorage.setItem('roleCode', this.selectedRole.role_code);
  }

  async setSelectedMarket() {
    this.marketList = await firstValueFrom(this.getPrimaryMarket(this.selectedRegion.name));
    if (this.marketList && this.marketList?.length > 0) {
      let market = this.marketList[0];
      let market_code = market.primary_market.code
      if (this.selectedProductPlatform.user_default_market) {
        let user_market_code = null;
        const market_code_list: string[] = this.selectedProductPlatform.user_default_market.split(',');
        market = this.marketList.find((market: Market) => {
          if (market_code_list.includes(market.primary_market.code)) {
            user_market_code = user_market_code ? `${user_market_code},${market.primary_market.code}` : market.primary_market.code
          }
        });
        if (user_market_code) {
          market_code = user_market_code;
        }
      }

      this.setSelectedMarketCode(market_code);
    } else {
      this.marketList = null;
      this.setSelectedMarketCode(null);
    }

    this.marketSubject.next();
  }

  // Markets per region are static reference data; the same request was fired
  // several times during page load. One shared, replayed request per region
  // serves all subscribers; failures are evicted so the next call retries.
  private readonly primaryMarketCache = new Map<string, Observable<Market[]>>();

  getPrimaryMarket(regionName: string): Observable<Market[]> {
    const key = regionName ?? '';
    const cached = this.primaryMarketCache.get(key);
    if (cached) {
      return cached;
    }
    const request$ = this.httpClient.get<Market[]>(`${ADMIN_SERVICE.getDetailMarketList}?region_name=${regionName}`).pipe(
      map(marketList => marketList.sort((a, b) => a.primary_market.name.localeCompare(b.primary_market.name))),
      catchError(err => {
        this.primaryMarketCache.delete(key);
        return throwError(() => err);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
      // Hand each subscriber its own copy so downstream sorting/mutation
      // cannot corrupt the shared cached array.
      map(marketList => [...marketList])
    );
    this.primaryMarketCache.set(key, request$);
    return request$;
  }
}